import { useTranslation } from "react-i18next";
import Logo from "../assets/img/logos/X.svg";
import DivixLogo from "../assets/img/logos/divixNegro.png";

import { Avatar, Checkbox, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AcceptTermsModal } from "../components/modals/AcceptTermsModal";
import { isHiltonCompany } from "../utils/hilton-branch";
import { ChangeAvatar } from "../components/teams/change-avatar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserService } from "../services/user-service";
import { useToast } from "../context/toast-context";
import { UnverifiedUser } from "../services/dto/response/user-response-dto";
import { DNIUploader } from "../components/DniUploader";
import { compressImage } from "../utils/compress-image";
import { isAlias, isCBU } from "../utils/helpers";
import { LoadingModal } from "../components/LoadingModal";
import profileGenericPic from "../assets/img/logos/profileGenericPic.jpg";
import { useAuth } from "../context/auth-context";
import { getCountryCode, COUNTRIES } from "../constants/countries";
import { DOCUMENT_LENGTHS } from "../constants/validations";
interface FormDataValues {
  birthDate: string;
  dni: string;
  identityType: "CUIL" | "CEDULA";
  bankCode?: string;
  identityNumber: string;
  bankAccountNumber: string;
  phone: string;
  address: string;
  password: string;
  confirmPassword: string;
  frontDNI: File | null;
  backDNI: File | null;
}

export function VerifyEmployee() {
  const [employee, setEmployee] = useState<UnverifiedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { country } = useAuth();
  const countryCode = getCountryCode(country);
  const isDominicanRepublic = countryCode === COUNTRIES.DO;
  const isHilton = isHiltonCompany(employee?.companyName);
   
  const defaultIdentityType = isDominicanRepublic ? "CEDULA" : "CUIL";
  const [isVerifying, setIsVerifying] = useState(false);
  const [frontDNI, setFrontDNI] = useState<File | null>(null);
  const [backDNI, setBackDNI] = useState<File | null>(null);
  const [profileImgFile, setProfileImgFile] = useState<File | null>(null);
  const [identityType, setIdentityType] = useState<"CUIL" | "CEDULA">(defaultIdentityType);
  const [selectedBankCode, setSelectedBankCode] = useState<string>("");
  const [selectedAccountTypeId, setSelectedAccountTypeId] = useState<string>("");

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userService = useMemo(() => new UserService(), []);

  const userid = params.get("userid");
  const token = params.get("token");
  const minPasswordLength = 6;
  
  // Detectar país para labels y validaciones dinámicos
  const dniLabel = isDominicanRepublic ? t("employee.verify.cedula") : t("employee.verify.dni");
  const cuitLabel = isDominicanRepublic ? t("employee.verify.cedula") : t("employee.verify.cuit");
  const bankAccountLabel = isDominicanRepublic ? t("employee.verify.bankAccountNumber") : t("employee.verify.cbu");
  const dniLength = isDominicanRepublic ? DOCUMENT_LENGTHS.DO_CEDULA_LENGTH : 8;
  const cuitLength = isDominicanRepublic ? DOCUMENT_LENGTHS.DO_CEDULA_LENGTH : DOCUMENT_LENGTHS.AR_CUIL_LENGTH;
  // Para República Dominicana, el identityNumber debe tener entre 8 y 11 dígitos
  const dniMinLength = isDominicanRepublic ? 8 : 8;
  const dniMaxLength = isDominicanRepublic ? 11 : 8;

  useEffect(() => {
    setIdentityType(defaultIdentityType);
  }, [defaultIdentityType]);

  const validate = (data: FormDataValues) => {
    let errors: { [key: string]: string } = {};
    let isValid = true;

    const { birthDate, dni, identityNumber, bankAccountNumber, phone, address, password, confirmPassword } = data;

    if (!birthDate) {
      errors.birthDate = t("employee.verify.errors.birthDate");
      isValid = false;
    } else {
      const birthDateObj = new Date(birthDate);
      const today = new Date();
      const age = today.getFullYear() - birthDateObj.getFullYear();

      //Verificación si todavia no cumple este año
      const isBeforeBirthdayThisYear =
        today.getMonth() < birthDateObj.getMonth() ||
        (today.getMonth() === birthDateObj.getMonth() &&
          today.getDate() < birthDateObj.getDate());

      if (age < 18 || (age === 18 && isBeforeBirthdayThisYear)) {
        errors.birthDate = t("employee.verify.errors.elderAge");
        isValid = false;
      }
    }

    if (!phone) {
      errors.phone = t("employee.verify.errors.phone-required");
      isValid = false;
    } else if (phone.length < 7 || phone.length > 20) {
      errors.phone = t("employee.verify.errors.phone-size");
      isValid = false;
    }

    if (!dni) {
      errors.dni = isDominicanRepublic ? t("employee.verify.errors.cedula") : t("employee.verify.errors.dni");
      isValid = false;
    } else if (isDominicanRepublic) {
      // Para República Dominicana: validar que tenga entre 8 y 11 dígitos
      if (dni.length < dniMinLength || dni.length > dniMaxLength) {
        errors.dni = t("employee.verify.errors.cedula");
        isValid = false;
      }
    } else {
      // Para Argentina: validar que tenga exactamente 8 dígitos
      if (dni.length !== dniLength) {
        errors.dni = t("employee.verify.errors.dni");
        isValid = false;
      }
    }

    if (!isDominicanRepublic) {
      if (!identityNumber || identityNumber.length !== cuitLength) {
        errors.identityNumber = t("employee.verify.errors.cuit");
        isValid = false;
      } else {
        // Validación de prefijo solo para Argentina (CUIL)
        const value = identityNumber.trim().replace(/\s/g, "");
        if (identityType === "CUIL") {
          const prefix = value.substring(0, 2);
          const validPrefixes = ["20", "23", "24", "27"];
          if (!validPrefixes.includes(prefix)) {
            errors.identityNumber = t("branches.create.errors.cuit-cuil-physical");
            isValid = false;
          }
        }
      }
    }

    if (!bankAccountNumber) {
      errors.bankAccountNumber = t("employee.verify.errors.cbu");
      isValid = false;
    } else {
      const cleanValue = bankAccountNumber.trim().replace(/\s/g, "");

      if (isDominicanRepublic) {
        // Para República Dominicana, debe tener entre 10 y 20 dígitos
        if (!/^\d{10,20}$/.test(cleanValue)) {
          errors.bankAccountNumber = t("employee.verify.errors.bankAccountNumber-10-20-digits");
          isValid = false;
        }
      } else {
        // Para Argentina: CBU debe tener 22 dígitos o Alias entre 6 y 20 caracteres
        const isCBU = /^\d{22}$/.test(cleanValue);
        const isAlias = /^[a-zA-Z0-9.]{6,20}$/.test(cleanValue);

        if (!isCBU && !isAlias) {
          errors.bankAccountNumber = t("employee.verify.errors.invalid-cbu-alias");
          isValid = false;
        }
      }
    }

    // Validar banco y tipo de cuenta para República Dominicana
    if (isDominicanRepublic) {
      if (!selectedBankCode) {
        errors.bankCode = t("employee.verify.errors.bankCode");
        isValid = false;
      }
      if (!selectedAccountTypeId) {
        errors.accountTypeId = t("employee.verify.errors.accountTypeId");
        isValid = false;
      }
    }

    if (!address?.trim()) {
      errors.address = t("managers.create.errors.address");
      isValid = false;
    } else if (address.length < 5) {
      errors.address = t("managers.create.errors.address-min");
      isValid = false;
    }

    if (!password || password.length < minPasswordLength) {
      errors.password = t("employee.verify.errors.password-min");
      isValid = false;
    }

    if (!confirmPassword) {
      errors.confirmPassword = t("employee.verify.errors.confirm-password-required");
      isValid = false;
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t("employee.verify.errors.passwords-mismatch");
      isValid = false;
    }

    if (!frontDNI || !backDNI) {
      errors.dniDocuments = isDominicanRepublic 
        ? t("employee.verify.errors.cedulaDocuments") 
        : t("employee.verify.errors.dniDocuments");
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTermsChecked((prev) => !prev);
  };

  const handleAcceptTerms = () => {
    setIsTermsChecked(true);
    setIsOpenModal(false);
  };

  const getAvatarSrc = () => {
    if (profileImgFile) {
      return URL.createObjectURL(profileImgFile);
    } else if (employee?.imageUrl) {
      return employee?.imageUrl;
    } else {
      return profileGenericPic
    }
  };

  const clearError = (fieldName: string) => {
    setErrors((prev) => {
      const { [fieldName]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsVerifying(true);

    const formData = new FormData(e.currentTarget);
    const dniValue = formData.get("dni")?.toString() || "";
    const data: FormDataValues = {
      birthDate: formData.get("birthDate")?.toString() || "",
      dni: dniValue,
      identityNumber: isDominicanRepublic
        ? dniValue
        : formData.get("identityNumber")?.toString() || "",
      identityType: isDominicanRepublic ? "CEDULA" : identityType,
      bankAccountNumber: formData.get("bankAccountNumber")?.toString() || "",
      phone: formData.get("phone")?.toString() || "",
      address: formData.get("address")?.toString() || "",
      password: formData.get("password")?.toString() || "",
      confirmPassword: formData.get("confirmPassword")?.toString() || "",
      frontDNI: frontDNI,
      backDNI: backDNI,
    };

    if (!validate(data)) {
      setIsVerifying(false);
      return;
    }

    const compressedFrontDNI = await compressImage(frontDNI!);
    const compressedBackDNI = await compressImage(backDNI!);
    const compressedProfileImg = profileImgFile
      ? await compressImage(profileImgFile)
      : null;

    const cleanBankAccount = data.bankAccountNumber.trim().replace(/\s/g, "");
    
    const payload: any = {
      UserId: Number(userid!),
      Token: token!,
      BirthDate: data.birthDate,
      IdentityNumber: data.identityNumber,
      IdentityType: data.identityType,
      Dni: data.dni,
      Phone: data.phone,
      Address: data.address,
      Password: data.password,
      DocumentFront: compressedFrontDNI,
      DocumentBack: compressedBackDNI,
      PhotoImage: compressedProfileImg,
    };

    if (isDominicanRepublic) {
      // En República Dominicana se envía BankAccountNumber, BankId y AccountTypeId
      const bankAccountNumber = cleanBankAccount.replace(/\D/g, "");
      if (bankAccountNumber.length > 0) {
        payload.BankAccountNumber = bankAccountNumber;
      }
      if (selectedBankCode) {
        payload.BankId = selectedBankCode;
      }
      if (selectedAccountTypeId) {
        payload.AccountTypeId = selectedAccountTypeId;
      }
    } else {
      // En Argentina se puede enviar CBU (22 dígitos) o Alias (6-20 caracteres alfanuméricos)
      const isCBUValue = isCBU(cleanBankAccount);
      const isAliasValue = isAlias(cleanBankAccount);

      // Solo incluir BankAccountNumber si es un CBU válido (solo dígitos)
      if (isCBUValue) {
        const bankAccountNumber = cleanBankAccount.replace(/\D/g, "");
        if (bankAccountNumber.length === 22) {
          payload.BankAccountNumber = bankAccountNumber;
        }
      }

      // Solo incluir Alias si es un Alias válido y NO es CBU
      if (isAliasValue && !isCBUValue) {
        payload.Alias = cleanBankAccount;
      }
    }

    await userService.verifyEmployee(
      payload,
      (data) => {
        setIsVerifying(false);
        navigate("/verified-employee", {
          state: {
            userId: userid,
            useQR: employee?.useQR,
            employeeName: employee?.name,
          },
        });
      },
      (error) => {
        setIsVerifying(false);
        showToast(error, "error");
      }
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchEmployee = async () => {
      setIsLoading(true);

      try {
        await userService.getUnverifiedUser(
          {
            token: token!,
            userid: Number(userid!),
          },
          (data) => { 
            setEmployee(data);
          },
          (error) => {
            setErrors({ verified: "Usuario verificado" });
          }
        );
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (userid && token) {
      fetchEmployee();
    }
  }, [userService, showToast, token, userid]);

  useEffect(() => {
    let objectUrl: string | null = null;

    if (profileImgFile) {
      objectUrl = URL.createObjectURL(profileImgFile);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [profileImgFile]);

  return (
    <main className="h-screen relative container mx-auto">
      <LoadingModal isOpen={isVerifying} />
      <Link to="/" className="absolute top-6 left-6">
        <img width={62} height={62} src={DivixLogo} alt="Divix Logo" />
      </Link>
      <div className="flex flex-col mx-auto justify-center items-center pt-24 pb-4">
        <img
          className="absolute top-6 right-6 md:static h-8 w-8 md:w-14 md:h-14"
          src={Logo}
          alt="Divix Logo"
        />
        {!!errors.verified && !isLoading && (
          <div className="mt-20 text-xl text-center font-semibold">
            <p>{errors.verified}</p>
          </div>
        )}
        {!errors.verified && isLoading && (
          <div className="mt-20 text-xl font-semibold">{t("loading")}</div>
        )}
        {!errors.verified && employee && !isLoading && (
          <>
            <form
              className="flex flex-col md:flex-row gap-12 md:gap-24 items-center max-w-sm md:max-w-4xl mt-8 w-full"
              id="verify-employee"
              onSubmit={handleSubmit}
            >
              <div className="flex-1 px-4 md:px-0 flex justify-center md:justify-end">
                <div className="flex flex-row justify-between items-center  md:flex md:flex-col md:items-start md:justify-center gap-5">
                  <div className="text-2xl h-fit relative">
                    <Avatar
                      sx={{
                        width: {
                          xs: "4rem",
                          md: "6rem",
                        },
                        height: {
                          xs: "4rem",
                          md: "6rem",
                        },
                        border: "1px solid #E0E0E0",
                      }}
                      src={getAvatarSrc()}
                      alt="Avatar Usuario"
                    />
                    <ChangeAvatar
                      employee={employee}
                      onConfirm={(file) => setProfileImgFile(file)}
                    />
                  </div>
                  <div className="flex flex-col w-full max-w-60 md:max-w-full md:gap-5">
                    <div className="pt-2 text-sm md:text-lg font-medium break-words md:break-normal">
                      {employee.name}
                    </div>
                    <div className="text-sm md:text-lg font-medium break-words md:break-normal">
                      {employee.email}
                    </div>
                    <div className="text-sm md:text-lg font-medium">
                      {employee.groupName ?? "Sin grupo"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block h-52 w-[2px] shrink-0 bg-primary " />
              <div className="w-[75%] h-[2px] shrink-0 bg-primary md:hidden" />
              <div className="px-4 md:px-0 flex-1 text-center mt-8">
                <TextField
                  label={t("employee.verify.birthDate")}
                  variant="standard"
                  onChange={(e) => clearError("birthDate")}
                  error={!!errors.birthDate}
                  helperText={errors.birthDate}
                  fullWidth
                  name="birthDate"
                  type="date"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />
                <TextField
                  label={t("employee.verify.phone")}
                  type="text"
                  name="phone"
                  onChange={(e) => clearError("phone")}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    let value = input.value;

                    value = value.replace(/[^\d\s+]/g, ""); // Permitimos numeros, espacios y +
                    value = value.replace(/\s+/g, " "); // No permitimos mas de 1 espacio consecutivo

                    // Si ingresa + que sea lo primero de la cadena
                    value = value.replace(/(?!^)\+/g, "");
                    if (value.includes("+") && value[0] !== "+") {
                      value = "+" + value.replace(/\+/g, "");
                    }

                    input.value = value;
                  }}
                  variant="standard"
                  fullWidth
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />
                <TextField
                  label={t("managers.create.address")}
                  name="address"
                  type="text"
                  onChange={(e) => clearError("address")}
                  error={!!errors.address}
                  helperText={errors.address}
                  variant="standard"
                  fullWidth
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d32f2f",
                      marginLeft: 0,
                      marginTop: "3px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                <TextField
                  label={dniLabel}
                  name="dni"
                  onChange={(e) => clearError("dni")}
                  error={!!errors.dni}
                  helperText={errors.dni}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    let value = input.value.replace(/[^0-9]/g, "");
                    // Para República Dominicana, permitir hasta 11 dígitos; para Argentina, máximo 8
                    const maxLength = isDominicanRepublic ? 11 : 8;
                    if (value.length > maxLength) {
                      value = value.substring(0, maxLength);
                    }
                    input.value = value;
                  }}
                  variant="standard"
                  fullWidth
                  type="number"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />
                {!isDominicanRepublic && (
                  <div className="flex gap-4">
                    <TextField
                      select
                      label={" "}
                      name="identityType"
                      value={identityType}
                      onChange={(e) => {
                        setIdentityType(e.target.value as "CUIL" | "CEDULA");
                        clearError("identityNumber");
                      }}
                      variant="standard"
                      sx={{
                        minWidth: 80,
                        "& .MuiInput-root": {
                          color: "#000",
                          fontFamily: "Montserrat",
                          "&:before": {
                            borderBottom: "1px solid #838383",
                          },
                          "&:hover:not(.Mui-disabled):before": {
                            borderBottomColor: "#000",
                          },
                        },
                        "& label.Mui-focused": {
                          color: "#838383",
                        },
                        "& .MuiInputLabel-standard": {
                          color: "#838383",
                          fontWeight: "300",
                          fontFamily: "Montserrat",
                          fontSize: "1rem",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "#838383",
                        },
                      }}
                    >
                      <MenuItem sx={{ fontFamily: "Montserrat" }} value="CUIL">
                        CUIL
                      </MenuItem>
                    </TextField>
                    <TextField
                      label={cuitLabel}
                      name="identityNumber"
                      onChange={(e) => clearError("identityNumber")}
                      error={!!errors.identityNumber}
                      helperText={errors.identityNumber}
                      onInput={(e) => {
                        const input = e.target as HTMLInputElement;
                        input.value = input.value.replace(/[^0-9]/g, "");
                      }}
                      variant="standard"
                      fullWidth
                      type="number"
                      sx={{
                        "& .MuiInput-root": {
                          color: "#000",
                          fontFamily: "Montserrat",
                          "&:before": {
                            borderBottom: "1px solid #838383",
                          },
                          "&:hover:not(.Mui-disabled):before": {
                            borderBottomColor: "#000",
                          },
                        },
                        "& label.Mui-focused": {
                          color: "#838383",
                        },
                        "& .MuiInputLabel-standard": {
                          color: "#838383",
                          fontWeight: "300",
                          fontFamily: "Montserrat",
                          fontSize: "1rem",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "#838383",
                        },
                      }}
                    />
                  </div>
                )}
                <TextField
                  label={bankAccountLabel}
                  name="bankAccountNumber"
                  onChange={(e) => clearError("bankAccountNumber")}
                  error={!!errors.bankAccountNumber}
                  helperText={errors.bankAccountNumber}
                  variant="standard"
                  fullWidth
                  type="text"
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    let value = input.value;
                    if (isDominicanRepublic) {
                      // Para República Dominicana: solo números, máximo 20 dígitos
                      value = value.replace(/[^\d]/g, "");
                      if (value.length > 20) {
                        value = value.substring(0, 20);
                      }
                    } else {
                      // Para Argentina: permite CBU (números) o Alias (alfanumérico con puntos)
                      value = value.replace(/[^a-zA-Z0-9.-]/g, "");
                    }
                    input.value = value;
                  }}
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                      WebkitTextFillColor: "#000",
                    },
                    "& input:-webkit-autofill:focus": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                      WebkitTextFillColor: "#000",
                    },
                  }}
                />
                {isDominicanRepublic && employee?.banks && employee.banks.length > 0 && (
                  <TextField
                    select
                    label={t("employee.verify.bank")}
                    name="bankCode"
                    value={selectedBankCode}
                    onChange={(e) => {
                      setSelectedBankCode(e.target.value);
                      clearError("bankCode");
                    }}
                    error={!!errors.bankCode}
                    helperText={errors.bankCode}
                    variant="standard"
                    fullWidth
                    sx={{
                      "& .MuiInput-root": {
                        color: "#000",
                        fontFamily: "Montserrat",
                        "&:before": {
                          borderBottom: "1px solid #838383",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottomColor: "#000",
                        },
                      },
                      "& label.Mui-focused": {
                        color: "#838383",
                      },
                      "& .MuiInputLabel-standard": {
                        color: "#838383",
                        fontWeight: "300",
                        fontFamily: "Montserrat",
                        fontSize: "1rem",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#838383",
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: "Montserrat" }}>
                      {t("employee.verify.select-bank")}
                    </MenuItem>
                    {employee.banks.map((bank) => (
                      <MenuItem key={bank.code} value={bank.code} sx={{ fontFamily: "Montserrat" }}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {isDominicanRepublic && employee?.accountTypes && employee.accountTypes.length > 0 && (
                  <TextField
                    select
                    label={t("employee.verify.accountType")}
                    name="accountTypeId"
                    value={selectedAccountTypeId}
                    onChange={(e) => {
                      setSelectedAccountTypeId(e.target.value);
                      clearError("accountTypeId");
                    }}
                    error={!!errors.accountTypeId}
                    helperText={errors.accountTypeId}
                    variant="standard"
                    fullWidth
                    sx={{
                      "& .MuiInput-root": {
                        color: "#000",
                        fontFamily: "Montserrat",
                        "&:before": {
                          borderBottom: "1px solid #838383",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottomColor: "#000",
                        },
                      },
                      "& label.Mui-focused": {
                        color: "#838383",
                      },
                      "& .MuiInputLabel-standard": {
                        color: "#838383",
                        fontWeight: "300",
                        fontFamily: "Montserrat",
                        fontSize: "1rem",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#838383",
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: "Montserrat" }}>
                      {t("employee.verify.select-accountType")}
                    </MenuItem>
                    {employee.accountTypes.map((accountType) => (
                      <MenuItem key={accountType.id} value={accountType.id} sx={{ fontFamily: "Montserrat" }}>
                        {accountType.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  label={t("employee.verify.password")}
                  name="password"
                  type="password"
                  onChange={(e) => clearError("password")}
                  error={!!errors.password}
                  helperText={errors.password}
                  variant="standard"
                  fullWidth
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />
                <TextField
                  label={t("employee.verify.confirm-password")}
                  name="confirmPassword"
                  type="password"
                  onChange={(e) => clearError("confirmPassword")}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  variant="standard"
                  fullWidth
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />
                <div className="w-full mt-4">
                  <DNIUploader
                    onFilesChange={(front, back) => {
                      setFrontDNI(front);
                      setBackDNI(back);
                    }}
                  />
                  {errors.dniDocuments && (
                    <p className="text-red mt-2 text-xs">
                      {errors.dniDocuments}
                    </p>
                  )}
                </div>
              </div>
            </form>
            <div className="w-full mx-auto mt-4">
              <div className="w-full flex justify-center gap-1 items-center text-center mx-auto mt-8 px-2">
                <Checkbox
                  checked={isTermsChecked}
                  onChange={handleCheckboxChange}
                  color="primary"
                  sx={{
                    color: "#000",
                    padding: 0,
                    "&.Mui-checked": {
                      color: "#EF0BB8",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 16,
                      margin: 0,
                      padding: 0,
                    },
                  }}
                />
                <div className="text-xs font-semibold">
                  {t("verify-employee.accept")}{" "}
                  <span
                    className="hover:underline cursor-pointer"
                    onClick={() => setIsOpenModal(true)}
                  >
                    {t("verify-employee.terms")}
                  </span>
                </div>
              </div>
              <div className="flex justify-center  items-center mt-8">
                <button
                  type="submit"
                  form="verify-employee"
                  disabled={!isTermsChecked || isVerifying}
                  className={`mx-auto leading-5 bg-black h-8 w-72 text-white font-light rounded-3xl py-1 px-24 disabled:opacity-50 ${
                    isTermsChecked
                      ? "hover:bg-black/90 opacity-100"
                      : "opacity-50"
                  }`}
                >
                  {isVerifying ? (
                    <CircularProgress
                      sx={{
                        color: "#fff",
                      }}
                      size="1.5rem"
                      className="animate-spin "
                    />
                  ) : (
                    t("employee.verify.submit")
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {isOpenModal && (
        <AcceptTermsModal
          onAccept={handleAcceptTerms}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          useHilton={isHilton}
        />
      )}
    </main>
  );
}
